import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Chip,
  Link,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import BuildIcon from '@mui/icons-material/Build';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { format } from 'date-fns';
import { VehicleDetailModal, VehicleFormDialog } from '@/components/vehicles';
import {
  useVehiclesPage,
  useVehicle,
  useVehicleHistory,
  type VehicleListItem,
  type VehicleDetail,
  type VehiclePageFilters,
} from '@/hooks/useVehicles';
import { useDebounce } from '@/hooks/useDebounce';
import { useEntitySync } from '@/hooks/useEntitySync';
import { useNotify } from '@/hooks/useNotify';
import {
  DataTable,
  type DataTableColumn,
  RegistrationPlate,
  VehicleMakeLogo,
} from '@/components/common';
import { EntityListResults } from '@/components/common/EntityListResults';
import { neutralGrey } from '@/styles/theme';
import type { VehicleDetails, ServiceHistoryEntry, Customer, TraficomData } from '@/types';
import { dateFnsLocale, formatNumber } from '@/utils/locale';
import { useUrlMultiSelectFilter } from '@/hooks/useUrlMultiSelectFilter';

const STATUS_COLORS: Record<string, string> = {
  no_service_history: 'warning.main',
  service_overdue: 'error.main',
  serviced_recently: 'success.main',
};

const STATUS_ICONS: Record<string, React.ElementType> = {
  no_service_history: HistoryToggleOffIcon,
  service_overdue: EventBusyIcon,
  serviced_recently: BuildIcon,
};

const ALL_FILTERS = ['no_service_history', 'service_overdue', 'serviced_recently'] as const;

const SERVER_SORT_BY_COLUMN = {
  registration: 'registrationNumber',
  makeModel: 'makeModel',
  mileage: 'mileage',
  lastService: 'lastServiceDate',
} as const;

type VehicleColumnSortKey = keyof typeof SERVER_SORT_BY_COLUMN;

function isVehicleColumnSortKey(key: string): key is VehicleColumnSortKey {
  return Object.hasOwn(SERVER_SORT_BY_COLUMN, key);
}

function columnSortKeyFor(sortBy: string | undefined): VehicleColumnSortKey {
  switch (sortBy) {
    case 'makeModel':
      return 'makeModel';
    case 'mileage':
      return 'mileage';
    case 'lastServiceDate':
      return 'lastService';
    default:
      return 'registration';
  }
}

// Map API response to frontend VehicleDetails type
function mapToVehicleDetails(vehicle: VehicleListItem): VehicleDetails {
  const nameParts = vehicle.customerName.trim().split(' ');
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.slice(1).join(' ') || '';

  const customer: Customer = {
    id: vehicle.customerId,
    firstName,
    lastName,
    email: '',
    phone: vehicle.customerPhone,
  };

  return {
    id: vehicle.id,
    customerId: vehicle.customerId,
    registrationNumber: vehicle.registrationNumber,
    make: vehicle.make ?? '',
    model: vehicle.model ?? '',
    year: vehicle.year ?? 0,
    vin: vehicle.vin ?? undefined,
    mileage: vehicle.mileage ?? undefined,
    customer,
    color: undefined,
    engineType: undefined,
    transmission: undefined,
    fuelType: undefined,
    lastServiceAt: vehicle.lastServiceDate ?? undefined,
    nextServiceDue: undefined,
    motExpiryDate: undefined,
    insuranceExpiryDate: undefined,
    notes: undefined,
    serviceCount: 0,
    totalSpentCents: 0,
    createdAt: vehicle.createdAt,
  };
}

// Map detailed API response (with traficomData) to frontend VehicleDetails type
function mapVehicleDetailToVehicleDetails(
  detail: VehicleDetail,
  serviceHistory: ServiceHistoryEntry[],
): VehicleDetails {
  const traficom = detail.traficomData as TraficomData | undefined;

  const mapFuelTypeToEngineType = (
    fuelType?: string,
  ): VehicleDetails['engineType'] => {
    if (!fuelType) return undefined;
    const lower = fuelType.toLowerCase();
    if (lower.includes('bensiini') || lower.includes('petrol'))
      return 'petrol';
    if (lower.includes('diesel')) return 'diesel';
    if (lower.includes('sähkö') || lower.includes('electric'))
      return 'electric';
    if (lower.includes('hybridi') || lower.includes('hybrid'))
      return 'hybrid';
    return undefined;
  };

  const mapTransmission = (
    transmission?: string,
  ): VehicleDetails['transmission'] => {
    if (!transmission) return undefined;
    const lower = transmission.toLowerCase();
    if (lower.includes('automaatti') || lower.includes('automatic'))
      return 'automatic';
    return 'manual';
  };

  const customer: Customer = detail.customer
    ? {
        id: detail.customer.id,
        firstName: detail.customer.firstName ?? '',
        lastName: detail.customer.lastName ?? '',
        email: detail.customer.email ?? '',
        phone: detail.customer.phone ?? '',
      }
    : {
        id: detail.customerId,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      };

  return {
    id: detail.id,
    customerId: detail.customerId,
    registrationNumber: detail.registrationNumber,
    make: detail.make ?? '',
    model: detail.model ?? '',
    year: detail.year ?? 0,
    vin: detail.vin ?? undefined,
    mileage: detail.mileage ?? undefined,
    customer,
    owner: detail.owner,
    holder: detail.holder,
    workOrderers: detail.workOrderers,
    color: traficom?.color,
    engineType: mapFuelTypeToEngineType(traficom?.fuelType),
    fuelType: traficom?.fuelType,
    transmission: mapTransmission(traficom?.transmission),
    lastServiceAt: detail.lastServiceDate ?? undefined,
    motExpiryDate: traficom?.nextInspectionDue,
    traficomData: traficom,
    serviceCount: serviceHistory.length,
    totalSpentCents: serviceHistory.reduce((sum, h) => sum + h.totalCents, 0),
    createdAt: detail.createdAt,
  };
}

export default function VehiclesPage() {
  const { t } = useTranslation('vehicles');
  const { t: tc } = useTranslation('common');
  const { t: tWorkorders } = useTranslation('workorders');

  // Still needed here for the ?id= deep link to the vehicle modal below.
  const [searchParams, setSearchParams] = useSearchParams();

  /*
    #878 extracted this into useUrlMultiSelectFilter — but nothing ever
    called the hook, so all four pages kept their own verbatim copy of
    ~50 lines (#2726). This is that migration, finally.
  */
  const { selectedFilters, setSelectedFilters } = useUrlMultiSelectFilter({
    allFilters: ALL_FILTERS,
    storageKey: 'vs-filter-vehicles',
  });

  const handleToggleFilter = useCallback((filterKey: string) => {
    const isSelected = selectedFilters.includes(filterKey);
    if (isSelected) {
      setSelectedFilters(selectedFilters.filter((s) => s !== filterKey));
    } else {
      setSelectedFilters([...selectedFilters, filterKey]);
    }
  }, [selectedFilters, setSelectedFilters]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 300);

  // One row per car (#2204). Page number, page size and sort go to the server.
  const listFilters = useMemo((): VehiclePageFilters => {
    if (!debouncedQuery) return { distinctVehicles: true };
    return { q: debouncedQuery, distinctVehicles: true };
  }, [debouncedQuery]);

  const {
    rows,
    total,
    isLoading,
    isError,
    error,
    refetch,
    pagination,
    sort,
    setSort,
  } = useVehiclesPage(listFilters);

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    if (!isVehicleColumnSortKey(key)) return;
    setSort({ sortBy: SERVER_SORT_BY_COLUMN[key], sortDir: direction });
  }, [setSort]);

  // UI state
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);

  // Fetch full vehicle detail (includes traficomData, owner, holder, etc.)
  const { data: vehicleDetail } = useVehicle(selectedVehicleId ?? undefined);

  // Fetch service history for selected vehicle
  const { data: historyData } = useVehicleHistory(selectedVehicleId ?? undefined);

  // Real-time entity sync (active when modal is open)
  const notify = useNotify();
  const { lastChange } = useEntitySync(
    modalOpen ? 'vehicle' : undefined,
    modalOpen ? (selectedVehicleId ?? undefined) : undefined,
  );
  const lastChangeRef = useRef(lastChange);
  useEffect(() => {
    if (lastChange && lastChange !== lastChangeRef.current) {
      const scopeKey = `realtime.scopeDesc.${lastChange.scope}`;
      notify.info(tc('realtime.updatedByOther'), tc(scopeKey));
    }
    lastChangeRef.current = lastChange;
  }, [lastChange, notify, tc]);

  // Client-side multi-select OR-filter on the current page.
  const allSelected = selectedFilters.length === ALL_FILTERS.length && ALL_FILTERS.every((s) => selectedFilters.includes(s));

  const filteredResults = useMemo(() => {
    if (allSelected) return rows;

    const now = Date.now();
    const twelveMonthsAgo = now - 365 * 24 * 60 * 60 * 1000;
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

    return rows.filter((v) => {
      // OR logic: show if vehicle matches ANY selected filter
      if (selectedFilters.includes('no_service_history') && !v.lastServiceDate) return true;
      if (selectedFilters.includes('service_overdue') && v.lastServiceDate && new Date(v.lastServiceDate).getTime() < twelveMonthsAgo) return true;
      if (selectedFilters.includes('serviced_recently') && v.lastServiceDate && new Date(v.lastServiceDate).getTime() >= thirtyDaysAgo) return true;
      return false;
    });
  }, [rows, selectedFilters, allSelected]);

  const hiddenByFilter = rows.length - filteredResults.length;

  // Find selected vehicle
  const selectedVehicle = useMemo(() => {
    if (!selectedVehicleId) return null;
    return rows.find((v) => v.id === selectedVehicleId) ?? null;
  }, [selectedVehicleId, rows]);

  // Map service history to frontend type
  const serviceHistory: ServiceHistoryEntry[] = useMemo(() => {
    if (!historyData?.data) return [];
    return historyData.data.map((h) => ({
      id: h.id,
      workOrderNumber: h.workOrderNumber ?? tWorkorders('noWorkOrderNumber'),
      date: h.completedAt ?? h.scheduledAt,
      mileage: h.mileage ?? 0,
      services: (h.serviceType ?? '').split(', ').filter(Boolean),
      totalCents: h.totalCostCents ?? 0,
      mechanicName: h.mechanicName ?? '',
      notes: undefined,
    }));
  }, [historyData, tWorkorders]);

  // Compute counts for chips
  const serviceMetrics = useMemo(() => {
    const now = Date.now();
    const twelveMonthsAgo = now - 365 * 24 * 60 * 60 * 1000;
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

    const noServiceHistoryCount = rows.filter((v) => !v.lastServiceDate).length;
    const overdueServiceCount = rows.filter((v) => {
      if (!v.lastServiceDate) return false;
      return new Date(v.lastServiceDate).getTime() < twelveMonthsAgo;
    }).length;
    const servicedLast30DaysCount = rows.filter((v) => {
      if (!v.lastServiceDate) return false;
      return new Date(v.lastServiceDate).getTime() >= thirtyDaysAgo;
    }).length;

    return { noServiceHistoryCount, overdueServiceCount, servicedLast30DaysCount };
  }, [rows]);

  // Status count map
  const statusCountMap = useMemo(() => ({
    no_service_history: serviceMetrics.noServiceHistoryCount,
    service_overdue: serviceMetrics.overdueServiceCount,
    serviced_recently: serviceMetrics.servicedLast30DaysCount,
  }), [serviceMetrics]);

  // Open modal when ?id= URL param is present (e.g. from GlobalSearch navigation)
  // Also close modal when ?id= param is removed (e.g. browser back button)
  useEffect(() => {
    const idParam = searchParams.get('id');
    if (idParam) {
      setSelectedVehicleId(idParam);
      setModalOpen(true);
    } else {
      setModalOpen(false);
      setSelectedVehicleId(null);
    }
  }, [searchParams]);

  // Handlers
  const handleViewVehicle = (vehicle: VehicleListItem) => {
    const next = new URLSearchParams(searchParams);
    next.set('id', vehicle.id);
    setSearchParams(next, { replace: true });
  };

  const handleCloseModal = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: true });
  };

  const handleAddVehicle = () => {
    setFormDialogOpen(true);
  };

  const handleFormDialogClose = () => {
    setFormDialogOpen(false);
  };

  // Table columns
  const columns: DataTableColumn<VehicleListItem>[] = [
    {
      header: t('vehicle'),
      sortKey: 'registration',
      renderCell: (vehicle) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <VehicleMakeLogo make={vehicle.make} />
          <Box>
            <RegistrationPlate
              registrationNumber={vehicle.registrationNumber}
            />
          </Box>
        </Box>
      ),
    },
    {
      header: t('makeModel'),
      sortKey: 'makeModel',
      renderCell: (vehicle) => (
        <>
          <Typography variant="body2" fontWeight={500}>
            {vehicle.make} {vehicle.model}
          </Typography>
          {vehicle.year && (
            <Typography variant="caption" color="text.secondary">
              {vehicle.year}
            </Typography>
          )}
        </>
      ),
    },
    {
      header: t('owner'),
      renderCell: (vehicle) => (
        <>
          <Typography variant="body2">{vehicle.customerName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {vehicle.customerPhone}
          </Typography>
        </>
      ),
    },
    {
      header: t('mileage'),
      sortKey: 'mileage',
      renderCell: (vehicle) => (
        <Typography variant="body2">
          {formatNumber(vehicle.mileage)} km
        </Typography>
      ),
    },
    {
      header: t('lastService'),
      sortKey: 'lastService',
      renderCell: (vehicle) =>
        vehicle.lastServiceDate ? (
          <Typography variant="body2" color="text.secondary">
            {format(new Date(vehicle.lastServiceDate), 'd.M.yyyy', {
              locale: dateFnsLocale(),
            })}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      header: '',
      align: 'center',
      renderCell: (vehicle) => (
        <Tooltip title={t('view')}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleViewVehicle(vehicle);
            }}
            aria-label={t('view')}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const renderMobileCard = (vehicle: VehicleListItem) => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <RegistrationPlate registrationNumber={vehicle.registrationNumber} />
      </Box>
      <Typography variant="body2" fontWeight={500}>
        {vehicle.make} {vehicle.model} {vehicle.year ?? ''}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {vehicle.customerName}
      </Typography>
    </Box>
  );

  return (
    <Box>
      {/* Header: title + add button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight={600}>
          {t('title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddVehicle}
        >
          {t('addVehicle')}
        </Button>
      </Box>

      {/* Search bar */}
      <TextField
        size="small"
        placeholder={t('searchPlaceholder')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ width: { xs: '100%', md: 500 }, mb: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <Tooltip title={t('clearSearch')}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    aria-label={t('clearSearch')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          },
        }}
      />

      {/* Multi-select vehicle filter chip toggles */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        {ALL_FILTERS.map((filterKey) => {
          const isSelected = selectedFilters.includes(filterKey);
          const count = statusCountMap[filterKey] ?? 0;
          const color = STATUS_COLORS[filterKey] ?? neutralGrey;
          const IconComp = STATUS_ICONS[filterKey] ?? HistoryToggleOffIcon;

          return (
            <Tooltip key={filterKey} title={t(`dashboard.${filterKey === 'no_service_history' ? 'noServiceHistory' : filterKey === 'service_overdue' ? 'serviceOverdue' : 'servicedLast30Days'}`)}>
              <Chip
                icon={<IconComp sx={{ fontSize: 16 }} />}
                label={count}
                size="small"
                onClick={() => handleToggleFilter(filterKey)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  minWidth: 52,
                  ...(isSelected
                    ? {
                        bgcolor: color,
                        color: 'common.white',
                        '& .MuiChip-icon': { color: 'common.white' },
                        '&:hover': { bgcolor: color, opacity: 0.85 },
                      }
                    : {
                        bgcolor: 'transparent',
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.secondary',
                        '& .MuiChip-icon': { color },
                        '&:hover': { bgcolor: 'action.hover' },
                      }),
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Results. The counter is the server total; chip filters still hide
          rows of this page only (#2204). */}
      <EntityListResults
        loading={isLoading}
        isError={isError}
        errorText={t('errorLoading')}
        error={error}
        onRefetch={() => { void refetch(); }}
        resultCountText={t('searchResultCount', { count: total })}
        showSelectStatusWarning={!isLoading && selectedFilters.length === 0}
        pagination={pagination}
      >
        {!isLoading && !allSelected && selectedFilters.length > 0 && hiddenByFilter > 0 && (
          <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
            {t('filterHiding', { count: hiddenByFilter })}{' '}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => { setSelectedFilters([...ALL_FILTERS]); }}
            >
              {t('showAll')}
            </Link>
          </Typography>
        )}

        <DataTable<VehicleListItem>
          columns={columns}
          data={filteredResults}
          getRowKey={(v) => v.id}
          sortKey={columnSortKeyFor(sort?.sortBy)}
          sortDirection={sort?.sortDir ?? 'asc'}
          onSortChange={handleSortChange}
          onRowClick={handleViewVehicle}
          emptyMessage={t('noVehicles')}
          emptyDescription={t('noVehiclesDesc')}
          loading={isLoading}
          renderMobileCard={renderMobileCard}
        />
      </EntityListResults>

      {/* Detail Modal */}
      <VehicleDetailModal
        vehicle={
          vehicleDetail
            ? mapVehicleDetailToVehicleDetails(vehicleDetail, serviceHistory)
            : selectedVehicle
              ? mapToVehicleDetails(selectedVehicle)
              : null
        }
        serviceHistory={serviceHistory}
        open={modalOpen}
        onClose={handleCloseModal}
      />

      {/* Add vehicle form — existing vehicles are edited inline on the card */}
      <VehicleFormDialog
        open={formDialogOpen}
        onClose={handleFormDialogClose}
      />
    </Box>
  );
}
